import { Link } from "react-router-dom";
import { useInView } from "../../../hooks/useInView";

const events = [
  {
    id: 1,
    title: "PWA: Summer Star Wars",
    subtitle: "Fight Night",
    date: "July 13, 2025",
    location: "Hawaiian Gardens, CA",
    image: "https://framerusercontent.com/images/WqNOWTjbmIVtjiaEDYeI45Ieo.png",
  },
  {
    id: 2,
    title: "PWA World Cup",
    subtitle: "Live at Metroflex",
    date: "February 10, 2025",
    location: "Hawaiian Gardens, CA",
    image: "https://framerusercontent.com/images/jMmDZ26wqsxYyRAHXrefJ7z43o.png",
  },
  {
    id: 3,
    title: "PWA Heavyweight Championship",
    subtitle: "Tournament",
    date: "July 13, 2024",
    location: "Hawaiian Gardens, CA",
    image: "https://framerusercontent.com/images/jJsap1mEztdkqsIDongd5h6zJjM.png",
  },
];

export default function PreviousEventsSection() {
  const { ref, inView } = useInView();

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className="bg-[#111111] py-16 md:py-24 relative overflow-hidden"
    >
      {/* Decorative elements */}
      <div className="absolute inset-0 bg-dot-grid-dark pointer-events-none" />
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-orange-500/6 rounded-full blur-3xl pointer-events-none animate-glow-pulse" />
      <div className="absolute bottom-0 left-0 w-80 h-px bg-gradient-to-r from-orange-500/30 to-transparent pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        {/* Section Header */}
        <div
          className={`flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 anim-hidden ${inView ? "animate-fade-in-up" : ""}`}
        >
          <div>
            <div className="inline-block border border-zinc-700 text-zinc-500 font-condensed font-semibold text-xs uppercase tracking-widest px-3 py-1 mb-4">
              Previous Events
            </div>
            <h2 className="font-condensed font-black text-white text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight">
              Event Recap
            </h2>
            {/* Animated underline */}
            <div
              className={`mt-3 h-0.5 bg-orange-500 w-16 anim-hidden ${inView ? "line-grow delay-300" : ""}`}
            />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <p className="text-zinc-500 text-sm max-w-xs">
              Catch up on every show — recaps, results, and highlights from every PWA night.
            </p>
            <Link
              to="/events"
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-condensed font-bold text-sm uppercase tracking-widest px-6 py-3 transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0 group"
            >
              View all Events
              <i className="ri-arrow-right-line transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        {/* 3-Column Card Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event, i) => (
            <Link
              key={event.id}
              to="/events"
              className={`group bg-zinc-800 border border-zinc-700 overflow-hidden hover:border-orange-500 transition-all duration-300 cursor-pointer block hover:-translate-y-1 anim-hidden ${
                inView ? "animate-fade-in-up" : ""
              }`}
              style={{ animationDelay: `${150 + i * 120}ms` }}
            >
              {/* Poster Image */}
              <div className="w-full h-64 overflow-hidden relative">
                <img
                  src={event.image}
                  alt={event.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                {/* Orange tint overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/80 via-orange-950/20 to-transparent" />
                {/* View arrow appears on hover */}
                <div className="absolute top-3 right-3 w-8 h-8 bg-orange-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-2 group-hover:translate-x-0">
                  <i className="ri-arrow-right-up-line text-white text-sm" />
                </div>
              </div>

              {/* Card Body */}
              <div className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-block bg-orange-500/20 text-orange-400 font-condensed font-semibold text-xs uppercase tracking-widest px-2 py-0.5">
                    Past Event
                  </span>
                </div>
                <h3 className="font-condensed font-black text-white text-xl uppercase leading-tight tracking-tight mb-1 group-hover:text-orange-400 transition-colors duration-200">
                  {event.title}
                </h3>
                <p className="font-condensed text-orange-500 text-sm uppercase tracking-wide font-semibold mb-3">
                  {event.subtitle}
                </p>
                <div className="flex items-center justify-between text-zinc-500 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-calendar-line" />
                    </div>
                    <span className="font-condensed uppercase tracking-wide">{event.date}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-map-pin-line" />
                    </div>
                    <span className="font-condensed uppercase tracking-wide">{event.location}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
